import { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Alert } from '@artprov/design-system'
import { createPortal } from 'react-dom'

const AlertMessage = ({
  show = false,
  variant = 'error',
  message = '',
  onClose,
  autoClose = 5000,
  floating = true,
  icon = 'cancel',
  iconStyle = 'text-error_10',
  className = '',
}) => {
  const [isVisible, setIsVisible] = useState(show)

  const handleClose = useCallback(() => {
    setIsVisible(false)
    if (onClose) {
      onClose()
    }
  }, [onClose])

  useEffect(() => {
    setIsVisible(show)
  }, [show])

  useEffect(() => {
    if (isVisible && autoClose) {
      const timer = setTimeout(() => {
        handleClose()
      }, autoClose)

      return () => clearTimeout(timer)
    }
  }, [isVisible, autoClose, handleClose])

  if (!isVisible) {
    return null
  }

  const floatingClasses = floating ? 'mfe-informes-root fixed top-24 right-4 z-[5000] max-w-md w-auto' : 'w-full'

  const lines = message ? message.split('\n') : []
  const title = lines[0] || message || 'Error'
  const description = lines.length > 1 ? lines.slice(1).join('\n') : undefined

  const content = (
    <div className={`${floatingClasses} ${className}`}>
      <Alert
        variant={variant}
        title={title}
        description={description}
        onClose={handleClose}
        interactive={false}
        icon={icon}
        iconStyle={iconStyle}
        autoDismiss={true}
        duration={autoClose}
      />
    </div>
  )

  if (floating && typeof document !== 'undefined') {
    return createPortal(content, document.body)
  }

  return content
}

AlertMessage.propTypes = {
  show: PropTypes.bool,
  variant: PropTypes.oneOf(['error', 'success', 'warning', 'neutral']),
  message: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  autoClose: PropTypes.number,
  floating: PropTypes.bool,
  icon: PropTypes.string,
  iconStyle: PropTypes.string,
  className: PropTypes.string,
}

export default AlertMessage
